import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { UserProfile } from '../../../types';
import { Plus, Trash2, Square, CheckSquare, ChevronLeft, ChevronRight, Search } from 'lucide-react';

interface BlogPost {
  id: number;
  title: string;
  status: string;
  author_id: number;
  created_at: number;
  categories: string | null;
}

interface Props {
    currentUser: UserProfile;
    blogPosts: BlogPost[];
    onDataNeededRefresh: () => void;
    totalCount?: number;
    currentPage?: number;
    onPageChange?: (page: number) => void;
    titleQuery: string;
    onTitleQueryChange: (query: string) => void;
    slugQuery: string;
    onSlugQueryChange: (query: string) => void;
    selectedCategoryIds: number[];
    onSelectedCategoryIdsChange: (ids: number[]) => void;
    createdFrom: string;
    createdTo: string;
    onCreatedFromChange: (value: string) => void;
    onCreatedToChange: (value: string) => void;
}

const formatDate = (ts: number) => new Date(ts).toLocaleString('pl-PL');

interface CategoryNode {
    id: number;
    name: string;
    parentId: number | null;
    children: CategoryNode[];
}

const ArticleList: React.FC<Props> = ({
    currentUser,
    blogPosts,
    onDataNeededRefresh,
    totalCount = 0,
    currentPage = 1,
    onPageChange,
    titleQuery,
    onTitleQueryChange,
    slugQuery,
    onSlugQueryChange,
    selectedCategoryIds,
    onSelectedCategoryIdsChange,
    createdFrom,
    createdTo,
    onCreatedFromChange,
    onCreatedToChange
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [lastSelectedId, setLastSelectedId] = useState<number | null>(null);
    const [categories, setCategories] = useState<Array<{ id: number; name: string; parent_id: number | null }>>([]);

    const adminPath = location.pathname.split('/')[1];

    const ITEMS_PER_PAGE = 50;
    const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

    useEffect(() => {
        setSelectedIds(new Set());
    }, [blogPosts]);

    // Fetch categories for filter
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const res = await fetch('/api/cms/blog-categories', {
                    headers: { 'x-admin-phone': currentUser.phone }
                });
                if (!res.ok) return;
                const data = await res.json();
                if (Array.isArray(data)) {
                    setCategories(
                        data.map((c: any) => ({
                            id: c.id,
                            name: c.name,
                            parent_id: typeof c.parent_id === 'number' ? c.parent_id : c.parent_id ?? null
                        }))
                    );
                }
            } catch (e) {
                console.error('Failed to fetch blog categories', e);
            }
        };
        fetchCategories();
    }, [currentUser.phone]);

    const handleSelectAll = () => {
        if (blogPosts.length > 0 && selectedIds.size === blogPosts.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(blogPosts.map(p => p.id)));
        }
    };

    const handleSelectRow = (id: number, e: React.MouseEvent) => {
        e.stopPropagation();
        const newSelected = new Set(selectedIds);
        
        if (e.shiftKey && lastSelectedId !== null) {
            const currentIndex = blogPosts.findIndex(p => p.id === id);
            const lastIndex = blogPosts.findIndex(p => p.id === lastSelectedId);
            const start = Math.min(currentIndex, lastIndex);
            const end = Math.max(currentIndex, lastIndex);
            for (let i = start; i <= end; i++) {
                if(blogPosts[i]?.id) newSelected.add(blogPosts[i].id);
            }
        } else {
            if (newSelected.has(id)) newSelected.delete(id);
            else newSelected.add(id);
        }

        setSelectedIds(newSelected);
        setLastSelectedId(id);
    };

    const handleBulkDelete = async () => {
        if (selectedIds.size === 0 || !confirm(`Delete ${selectedIds.size} post(s)?`)) return;
        try {
            await fetch('/api/cms/blog', {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json', 'x-admin-phone': currentUser.phone },
                body: JSON.stringify({ ids: Array.from(selectedIds) })
            });
            onDataNeededRefresh();
        } catch (e) { console.error(e); }
    };

    const blogColumns = [
        { key: 'id', label: 'ID', width: '60px' },
        { key: 'title', label: 'Title' },
        { key: 'categories', label: 'Categories' },
        { key: 'status', label: 'Status', width: '120px' },
        { key: 'created_at', label: 'Created At', width: '180px' },
    ];

    // Build category tree for hierarchical display
    const buildCategoryTree = (): CategoryNode[] => {
        const nodeMap = new Map<number, CategoryNode>();
        categories.forEach(cat => {
            nodeMap.set(cat.id, {
                id: cat.id,
                name: cat.name,
                parentId: cat.parent_id ?? null,
                children: []
            });
        });

        const roots: CategoryNode[] = [];
        nodeMap.forEach(node => {
            if (node.parentId != null && nodeMap.has(node.parentId)) {
                nodeMap.get(node.parentId)!.children.push(node);
            } else {
                roots.push(node);
            }
        });

        return roots;
    };

    const categoryTree = buildCategoryTree();

    const collectDescendantIds = (node: CategoryNode, acc: number[] = []): number[] => {
        acc.push(node.id);
        node.children.forEach(child => collectDescendantIds(child, acc));
        return acc;
    };

    const findNodeById = (id: number, nodes: CategoryNode[]): CategoryNode | null => {
        for (const node of nodes) {
            if (node.id === id) return node;
            const found = findNodeById(id, node.children);
            if (found) return found;
        }
        return null;
    };

    const handleCategoryToggle = (id: number) => {
        const node = findNodeById(id, categoryTree);
        if (!node) {
            // Fallback: toggle only this id
            const next = selectedCategoryIds.includes(id)
                ? selectedCategoryIds.filter(v => v !== id)
                : [...selectedCategoryIds, id];
            onSelectedCategoryIdsChange(next);
            return;
        }

        const allIds = collectDescendantIds(node, []);
        const current = new Set(selectedCategoryIds);
        const isSelected = current.has(id);

        if (isSelected) {
            // Unselect parent and all descendants
            allIds.forEach(catId => current.delete(catId));
        } else {
            // Select parent and all descendants
            allIds.forEach(catId => current.add(catId));
        }

        onSelectedCategoryIdsChange(Array.from(current));
    };

    const renderCategoryNode = (node: CategoryNode, depth: number = 0) => {
        const isChecked = selectedCategoryIds.includes(node.id);
        return (
            <div key={node.id}>
                <label
                    className="flex items-center gap-2 py-0.5 cursor-pointer text-xs text-gray-200"
                    style={{ paddingLeft: depth * 12 }}
                >
                    <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleCategoryToggle(node.id)}
                        className="h-3 w-3 rounded border-gray-600 bg-[#111]"
                    />
                    <span className="truncate">
                        {node.name}
                    </span>
                </label>
                {node.children.map(child => renderCategoryNode(child, depth + 1))}
            </div>
        );
    };

    return (
        <div className="w-full relative animate-fade-in flex flex-col h-full">
            <div className="mb-4">
                <div className="md:flex md:gap-4">
                    {/* Left column: basic inputs */}
                    <div className="md:w-2/3 space-y-2">
                        <div className="relative">
                            <input 
                                type="text"
                                placeholder="Tytuł artykułu..."
                                value={titleQuery}
                                onChange={e => onTitleQueryChange(e.target.value)}
                                className="bg-mystic-bg border border-gray-800 rounded-sm px-3 py-1.5 text-sm w-full pl-8"
                            />
                            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500" />
                        </div>
                        <div>
                            <input 
                                type="text"
                                placeholder="Slug artykułu..."
                                value={slugQuery}
                                onChange={e => onSlugQueryChange(e.target.value)}
                                className="bg-mystic-bg border border-gray-800 rounded-sm px-3 py-1.5 text-sm w-full"
                            />
                        </div>
                        <div className="flex gap-2">
                            <input
                                type="date"
                                value={createdFrom}
                                onChange={e => onCreatedFromChange(e.target.value)}
                                className="bg-mystic-bg border border-gray-800 rounded-sm px-2 py-1.5 text-sm w-1/2"
                            />
                            <input
                                type="date"
                                value={createdTo}
                                onChange={e => onCreatedToChange(e.target.value)}
                                className="bg-mystic-bg border border-gray-800 rounded-sm px-2 py-1.5 text-sm w-1/2"
                            />
                        </div>
                    </div>
                    {/* Right column: hierarchical categories */}
                    <div className="mt-3 md:mt-0 md:w-1/3">
                        <div className="text-xs font-bold text-gray-400 mb-1">
                            Kategorie (kliknięcie rodzica zaznacza/odznacza też podkategorie)
                        </div>
                        <div className="bg-mystic-bg border border-gray-800 rounded-sm px-2 py-2 text-xs max-h-40 overflow-auto">
                            {categoryTree.length === 0 ? (
                                <div className="text-gray-600">Brak kategorii</div>
                            ) : (
                                categoryTree.map(node => renderCategoryNode(node))
                            )}
                        </div>
                    </div>
                </div>
            </div>
             <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-4">
                    <button 
                        onClick={() => navigate(`/${adminPath}/blog/new`)}
                        className="flex items-center gap-1 bg-green-900/40 text-green-400 border border-green-800 hover:bg-green-900/60 px-3 py-1 rounded text-xs font-bold uppercase"
                    >
                        <Plus className="w-4 h-4" /> New Article
                    </button>
                    {selectedIds.size > 0 && (
                        <div className="flex items-center gap-2 bg-blue-900/30 border border-blue-700/50 rounded px-2 py-1">
                            <span className="text-xs text-blue-200">{selectedIds.size} selected</span>
                            <button onClick={handleBulkDelete} className="text-red-400 hover:text-red-300 p-1">
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    )}
                </div>
                
                {/* Pagination Stats */}
                <div className="text-xs text-gray-500">
                    Total: {totalCount} | Page {currentPage} of {totalPages || 1}
                </div>
            </div>

            <div className="overflow-x-auto border border-gray-800 rounded-sm">
                <table className="w-full text-left border-collapse">
                    <thead className="bg-mystic-box">
                        <tr className="border-b border-gray-800 text-gray-500 text-[10px] uppercase font-bold tracking-wider">
                            <th className="p-2 border-r border-gray-800 w-10 text-center">
                                <div onClick={handleSelectAll} className="cursor-pointer flex justify-center">
                                    {blogPosts.length > 0 && selectedIds.size === blogPosts.length ? <CheckSquare className="w-4 h-4 text-blue-400" /> : <Square className="w-4 h-4" />}
                                </div>
                            </th>
                            {blogColumns.map(col => (
                                <th key={col.key} className="p-2 border-r border-gray-800 last:border-r-0" style={{ width: col.width }}>
                                    {col.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800 bg-mystic-bg">
                        {blogPosts.map(post => {
                            const isSelected = selectedIds.has(post.id);
                            return (
                                <tr key={post.id} className={`group hover:bg-mystic-btn ${isSelected ? 'bg-blue-900/10' : ''}`}>
                                    <td className="p-2 border-r border-gray-800 text-center">
                                        <div onClick={(e) => handleSelectRow(post.id, e)} className="cursor-pointer flex justify-center">
                                            {isSelected ? <CheckSquare className="w-4 h-4 text-blue-400" /> : <Square className="w-4 h-4 text-gray-700 group-hover:text-gray-500" />}
                                        </div>
                                    </td>
                                    {blogColumns.map(col => (
                                        <td key={col.key} className="p-2 border-r border-gray-800 last:border-r-0 text-xs text-gray-300">
                                            {(() => {
                                                switch (col.key) {
                                                    case 'title':
                                                        return (
                                                            <button onClick={() => navigate(`/${adminPath}/blog/edit/${post.id}`)} className="font-bold text-mystic-accent hover:underline text-left">
                                                                {post.title}
                                                            </button>
                                                        );
                                                    case 'status':
                                                        return (
                                                            <span className={`px-2 py-0.5 rounded-full font-bold text-xs ${post.status === 'published' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                                                                {post.status}
                                                            </span>
                                                        );
                                                    case 'created_at':
                                                        return <span className="text-gray-400">{formatDate(post.created_at)}</span>;
                                                    case 'categories':
                                                        return (
                                                            <div className="flex flex-wrap gap-1">
                                                                {post.categories ? (
                                                                    post.categories.split(',').sort().map(cat => (
                                                                        <span key={cat} className="bg-gray-700 text-gray-300 text-[10px] px-1.5 py-0.5 rounded-full">
                                                                            {cat}
                                                                        </span>
                                                                    ))
                                                                ) : (
                                                                    <span className="text-gray-600">-</span>
                                                                )}
                                                            </div>
                                                        );
                                                    default:
                                                        return post[col.key as keyof BlogPost];
                                                }
                                            })()}
                                        </td>
                                    ))}
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && onPageChange && (
                <div className="flex justify-center items-center gap-4 py-4 mt-2">
                    <button 
                        onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="p-1 rounded bg-mystic-box border border-gray-800 text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                        <ChevronLeft className="w-5 h-5" />
                    </button>
                    
                    <span className="text-xs font-bold text-gray-400">
                        Page {currentPage} of {totalPages}
                    </span>

                    <button 
                        onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className="p-1 rounded bg-mystic-box border border-gray-800 text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                        <ChevronRight className="w-5 h-5" />
                    </button>
                </div>
            )}
        </div>
    );
};

export default ArticleList;